import { runInAction } from "mobx";
import { APIClient } from "Application";
import toast from "react-hot-toast";
import moment from "moment";

import { WeeklyRotaDetailsViewModel } from "../WeeklyRotaDetailViewModel";
import { GETRotaByDateEndPoint, GETRotaByDateRequest, GETRotaByDateResponse } from "../../EndPoint/GETRotaByDateEndPoint";
import { GETLeftStudentSessionEndPoint, GETLeftStudentSessionResponse } from "../../EndPoint/GETLeftStudentSessionEndPoint";
import { DeleteMentorStudentEndPoint, IDeleteMentorStudentRequest } from "../AddStudent/EndPoints/DeleteMentorStudentEndPoint";
import { DeleteMentorAbsenceEndpoint, DeleteMentorAbsenceRequest } from "../MentorAbsence/EndPoints/DeleteMentorAbsenceEndPoint";
import { GETMentorStudentRotaByIdEndpoint, MentorStudentRotaResponse } from "../AddStudent/EndPoints/GETMentorStudentRotaByIdEndpoint";
import { RemoveDetailsMentorStudentEndPoint, RemoveDetailsRequest } from "../AddStudent/EndPoints/RemoveDetailsMentorStudentEndPoint";
import { ResetWeeklyRotaEndPoint } from "../AddStudent/EndPoints/ResetWeeklyRotaEndPoint";
import { MentorStatusEnum } from "Views/Mentors/Mentors/Enums/MentorStatusEnum";

export class APIManager {
    public apiClient = new APIClient();

    constructor(private viewModel: WeeklyRotaDetailsViewModel) {}

    /** Loads the rota grid for the current start date. */
    public async getRotaByDateAsync(loadLeftStudentSessions: boolean = false): Promise<void> {
        this.viewModel.managers.ui.setBusy(true);

        const request: GETRotaByDateRequest = {
            startDate: moment(this.viewModel.model.startDate).format("YYYY-MM-DD"),
            mentorStatusType: MentorStatusEnum.Current,
        };
        await this.apiClient.sendAsync(new GETRotaByDateEndPoint(), request);

        if (this.apiClient.IsRequestSuccessful) {
            const response = this.apiClient.Response<GETRotaByDateResponse>();
            runInAction(() => {
                this.viewModel.model.rotaId = response.rota !== null ? response.rota?.id : null;
                this.viewModel.model.mentorAvailability = response.rota !== null ? response.mentorAvailability : [];
            });
            if (loadLeftStudentSessions) {
                await this.loadLeftStudentSession();
            }
            runInAction(() => {
                this.viewModel.managers.mobx.setSuccessfullyGetWeeklyRota(true);
            });
        } else {
            toast.error(this.apiClient.ValidationMessage);
        }

        this.viewModel.managers.ui.setBusy(false);
    }

    /** Loads remaining unassigned student sessions for the week banner. */
    public async loadLeftStudentSession(): Promise<void> {
        const startDate = moment(this.viewModel.model.startDate).format("YYYY-MM-DD");
        await this.apiClient.sendAsync(new GETLeftStudentSessionEndPoint(startDate));
        if (this.apiClient.IsRequestSuccessful) {
            const response = this.apiClient.Response<GETLeftStudentSessionResponse>();
            if (response) {
                runInAction(() => {
                    this.viewModel.model.leftStudentSession = response.leftStudentList;
                    this.viewModel.model.studentList = response.studentList;
                });
            }
        }
    }

    /** Removes a student from a mentor's slot, then refreshes the grid. */
    public async deleteStudentMentor(deleteMentorStudentId: Guid, studentSessionId: Guid): Promise<void> {
        // Guard against a concurrent removal; only one delete may be in flight at a time.
        if (this.viewModel.managers.mobx.deletingStudentSessionId !== null) {
            return;
        }
        this.viewModel.managers.mobx.setDeletingStudentSessionId(studentSessionId);

        const request: IDeleteMentorStudentRequest = {
            id: deleteMentorStudentId,
            studentSessionId: studentSessionId,
            startDate: moment(this.viewModel.model.startDate).format("YYYY-MM-DD"),
            isAbsence: false,
        };

        await this.apiClient.sendAsync(new DeleteMentorStudentEndPoint(), request);

        if (this.apiClient.IsRequestSuccessful) {
            await this.getRotaByDateAsync(true);
        } else {
            toast.error(this.apiClient.ValidationMessage);
        }

        this.viewModel.managers.mobx.setDeletingStudentSessionId(null);
    }

    /** Deletes a mentor absence record, then refreshes the grid. */
    public async deleteMentorAbsence(id: Guid): Promise<void> {
        this.viewModel.managers.ui.setIsProcessing(true);

        const request = new DeleteMentorAbsenceRequest();
        request.id = id;
        request.isAbsence = true;

        await this.apiClient.sendAsync(new DeleteMentorAbsenceEndpoint(), request);

        if (this.apiClient.IsRequestSuccessful) {
            await this.getRotaByDateAsync(true);
        } else {
            toast.error(this.apiClient.ValidationMessage);
        }

        this.viewModel.managers.ui.setIsProcessing(false);
    }

    /** Removes session detail fields from a mentor-student rota entry, then refreshes. */
    public async removeDetails(studentSessionId: Guid): Promise<void> {
        this.viewModel.managers.ui.setIsProcessing(true);

        const request: RemoveDetailsRequest = { studentSessionId };
        await this.apiClient.sendAsync(new RemoveDetailsMentorStudentEndPoint(), request);

        if (this.apiClient.IsRequestSuccessful) {
            await this.getRotaByDateAsync(true);
        } else {
            toast.error(this.apiClient.ValidationMessage);
        }

        this.viewModel.managers.ui.setIsProcessing(false);
    }

    /** Loads detail for a mentor-student rota entry by its ID, then refreshes. */
    public async getMentorStudentRotaById(mentorStudentRotaId: string): Promise<void> {
        if (!mentorStudentRotaId) return;

        this.viewModel.managers.ui.setIsProcessing(true);
        await this.apiClient.sendAsync(new GETMentorStudentRotaByIdEndpoint(mentorStudentRotaId));

        if (this.apiClient.IsRequestSuccessful) {
            const response = this.apiClient.Response<MentorStudentRotaResponse>();
            if (response) {
                runInAction(() => {
                    this.viewModel.model.studentRota.fromResponse(response);
                });
            }
            await this.getRotaByDateAsync(true);
        } else {
            toast.error(this.apiClient.ValidationMessage);
        }

        this.viewModel.managers.ui.setIsProcessing(false);
    }

    /** Resets the weekly rota for the current week, then refreshes. */
    public async resetRota(): Promise<void> {
        this.viewModel.managers.ui.setBusy(true);

        const startDate = moment(this.viewModel.model.startDate).format("YYYY-MM-DD");
        await this.apiClient.sendAsync(new ResetWeeklyRotaEndPoint(startDate));

        if (this.apiClient.IsRequestSuccessful) {
            await this.getRotaByDateAsync();
        } else {
            toast.error(this.apiClient.ValidationMessage);
        }

        this.viewModel.managers.ui.setBusy(false);
    }
}
